import cloneDeep from 'lodash.clonedeep';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import client from 'src/services/client';
import { IValidation, LoadingStatus } from 'src/types/main';
import { defaultValidation } from '~/constants/main';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';

interface IWysiwygData {
  template: string;
}

export default class WebsiteInfoWysiwygStore {
  private getRequestPath;
  private updateRequestPath;
  private storeTitle;

  constructor(getRequestPath: string, updateRequestPath: string, storeTitle: string) {
    makeObservable(this);
    this.getRequestPath = getRequestPath;
    this.updateRequestPath = updateRequestPath;
    this.storeTitle = storeTitle;
  }

  private getWysiwygDataAbortController = getAbortController();

  @observable private loading: LoadingStatus = LoadingStatus.Init;
  @observable public isEdit: boolean = false;
  @observable public wysiwygData: IWysiwygData | null = null;
  @observable public validation: IValidation = cloneDeep(defaultValidation);

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loading);
  }

  @action public init = () => {
    this.loading = LoadingStatus.Init;
    this.isEdit = false;
    this.validation = cloneDeep(defaultValidation);

    this.getWysiwygData();
  };

  @action public getWysiwygData = flow(function* (this: WebsiteInfoWysiwygStore) {
    this.getWysiwygDataAbortController.abort();
    this.getWysiwygDataAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      this.wysiwygData = yield client.get(this.getRequestPath, { signal: this.getWysiwygDataAbortController.signal });
      this.validation = cloneDeep(defaultValidation);

      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: `${this.storeTitle} getWysiwygData`, showModal: true });
    }
  }).bind(this);

  @action public updateWysiwygData = flow(function* (this: WebsiteInfoWysiwygStore) {
    try {
      this.loading = LoadingStatus.Loading;

      const response = yield client.put(this.updateRequestPath, { text: this.wysiwygData?.template });

      this.wysiwygData = { ...this.wysiwygData, template: response.text };
      this.isEdit = false;
      this.loading = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loading = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: `${this.storeTitle} updateWysiwygData`, showModal: true });
    }
  }).bind(this);

  @action public changeWysiwygDataHandler = (value: Partial<IWysiwygData>) => {
    if (!this.wysiwygData) return;

    this.validation = cloneDeep(defaultValidation);
    this.wysiwygData = { ...this.wysiwygData, ...value };

    this.isEdit = true;
  };

  private isErrorResponseHandled(error: any): boolean {
    const errorsBag = error.response.data;

    this.validation = cloneDeep(defaultValidation);
    let hasApiErrors = false;

    if (errorsBag.text) {
      this.validation.errorText = errorsBag.text[0];
      this.validation.isValid = false;
      hasApiErrors = true;
    }

    return hasApiErrors;
  }
}
